import React, { useMemo, useState } from "react";

import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

import {
  AddOutlined,
  AdminPanelSettingsOutlined,
  Clear,
  CloseOutlined,
  EditOutlined,
  GroupsOutlined,
  KeyOutlined,
  LockOutlined,
  ManageAccountsOutlined,
  PersonAddAltOutlined,
  SearchOutlined,
  SecurityOutlined,
  SettingsOutlined,
  ShieldOutlined,
} from "@mui/icons-material";

/* =========================================================
   COLORS
========================================================= */

const PRIMARY = "#008B8B";
const PRIMARY_LIGHT = "#00A6A6";
const PRIMARY_DARK = "#006F6F";

const TEXT = "#333333";
const MUTED = "#667085";
const BACKGROUND = "#F6F8F9";
const BORDER = "#E1E7E8";
const PAPER = "#FFFFFF";

/* =========================================================
   MODULES
========================================================= */

const modules = [
  {
    id: "dashboard",
    name: "Dashboard",
    description: "Dashboard and business overview",
  },
  {
    id: "crm",
    name: "CRM",
    description: "Customers, leads and contacts",
  },
  {
    id: "sales",
    name: "Sales",
    description: "Quotations, invoices and sales",
  },
  {
    id: "purchase",
    name: "Purchase",
    description: "Vendors and purchase management",
  },
  {
    id: "expenses",
    name: "Expenses",
    description: "Business expenses",
  },
  {
    id: "projects",
    name: "Projects",
    description: "Projects and task management",
  },
  {
    id: "support",
    name: "Support",
    description: "Customer support and tickets",
  },
  {
    id: "reports",
    name: "Reports",
    description: "Reports and analytics",
  },
  {
    id: "masters",
    name: "Masters",
    description: "Master data management",
  },
  {
    id: "settings",
    name: "Settings",
    description: "System configuration",
  },
];

const permissionTypes = [
  "view",
  "add",
  "edit",
  "delete",
  "export",
];

/* =========================================================
   PERMISSION OBJECT
========================================================= */

function createPermissionObject() {
  const permissions = {};

  modules.forEach((module) => {
    permissions[module.id] = {
      view: false,
      add: false,
      edit: false,
      delete: false,
      export: false,
    };
  });

  return permissions;
}

function createFullPermissions() {
  const permissions = createPermissionObject();

  modules.forEach((module) => {
    permissionTypes.forEach((permission) => {
      permissions[module.id][permission] = true;
    });
  });

  return permissions;
}

function createAdminPermissions() {
  const permissions = createPermissionObject();

  modules.forEach((module) => {
    permissions[module.id].view = true;
    permissions[module.id].add = true;
    permissions[module.id].edit = true;
    permissions[module.id].export = true;
  });

  return permissions;
}

function createManagerPermissions() {
  const permissions = createPermissionObject();

  [
    "dashboard",
    "crm",
    "sales",
    "purchase",
    "expenses",
    "projects",
    "support",
    "reports",
  ].forEach((moduleId) => {
    permissions[moduleId].view = true;
    permissions[moduleId].add = true;
    permissions[moduleId].edit = true;
    permissions[moduleId].export = true;
  });

  return permissions;
}

function createSalesPermissions() {
  const permissions = createPermissionObject();

  ["dashboard", "crm", "sales"].forEach(
    (moduleId) => {
      permissions[moduleId].view = true;
      permissions[moduleId].add = true;
      permissions[moduleId].edit = true;
      permissions[moduleId].export = true;
    }
  );

  return permissions;
}

function createAccountsPermissions() {
  const permissions = createPermissionObject();

  [
    "dashboard",
    "purchase",
    "expenses",
    "reports",
  ].forEach((moduleId) => {
    permissions[moduleId].view = true;
    permissions[moduleId].add = true;
    permissions[moduleId].edit = true;
    permissions[moduleId].export = true;
  });

  return permissions;
}

function createSupportPermissions() {
  const permissions = createPermissionObject();

  ["dashboard", "crm", "support"].forEach(
    (moduleId) => {
      permissions[moduleId].view = true;
      permissions[moduleId].add = true;
      permissions[moduleId].edit = true;
    }
  );

  return permissions;
}

/* =========================================================
   INITIAL ROLES
========================================================= */

const initialRoles = [
  {
    id: 1,
    name: "Super Admin",
    description:
      "Full system access with all permissions",
    users: 1,
    status: "Active",
    type: "System",
    permissions: createFullPermissions(),
  },
  {
    id: 2,
    name: "Administrator",
    description:
      "Administrative access to most modules",
    users: 1,
    status: "Active",
    type: "System",
    permissions: createAdminPermissions(),
  },
  {
    id: 3,
    name: "Manager",
    description:
      "Management access for business operations",
    users: 1,
    status: "Active",
    type: "Custom",
    permissions: createManagerPermissions(),
  },
  {
    id: 4,
    name: "Sales Executive",
    description:
      "Sales and CRM related access",
    users: 2,
    status: "Active",
    type: "Custom",
    permissions: createSalesPermissions(),
  },
  {
    id: 5,
    name: "Accountant",
    description:
      "Accounts, expenses and reports access",
    users: 1,
    status: "Active",
    type: "Custom",
    permissions: createAccountsPermissions(),
  },
  {
    id: 6,
    name: "Support Executive",
    description:
      "Customer support and CRM access",
    users: 1,
    status: "Inactive",
    type: "Custom",
    permissions: createSupportPermissions(),
  },
];

/* =========================================================
   EMPTY ROLE
========================================================= */

const emptyRole = {
  name: "",
  description: "",
  status: "Active",
};

/* =========================================================
   MAIN COMPONENT
========================================================= */

function RolesPermissions() {
  const [roles, setRoles] =
    useState(initialRoles);

  const [selectedRoleId, setSelectedRoleId] =
    useState(1);

  const [search, setSearch] =
    useState("");

  const [statusFilter, setStatusFilter] =
    useState("All");

  const [permissions, setPermissions] =
    useState(
      initialRoles[0].permissions
    );

  const [openDialog, setOpenDialog] =
    useState(false);

  const [editingRole, setEditingRole] =
    useState(null);

  const [roleForm, setRoleForm] =
    useState(emptyRole);

  /* =======================================================
     SELECTED ROLE
  ======================================================= */

  const selectedRole =
    roles.find(
      (role) =>
        role.id === selectedRoleId
    ) || roles[0];

  /* =======================================================
     SUMMARY
  ======================================================= */

  const totalRoles = roles.length;

  const activeRoles = roles.filter(
    (role) =>
      role.status === "Active"
  ).length;

  const adminRoles = roles.filter(
    (role) =>
      role.name === "Super Admin" ||
      role.name === "Administrator"
  ).length;

  const customRoles = roles.filter(
    (role) =>
      role.type === "Custom"
  ).length;

  /* =======================================================
     FILTER ROLES
  ======================================================= */

  const filteredRoles = useMemo(() => {
    return roles.filter((role) => {
      const text =
        search.toLowerCase().trim();

      const matchesSearch =
        !text ||
        role.name
          .toLowerCase()
          .includes(text) ||
        role.description
          .toLowerCase()
          .includes(text);

      const matchesStatus =
        statusFilter === "All" ||
        role.status === statusFilter;

      return (
        matchesSearch &&
        matchesStatus
      );
    });
  }, [
    roles,
    search,
    statusFilter,
  ]);

  /* =======================================================
     SELECT ROLE
  ======================================================= */

  const handleSelectRole = (role) => {
    setSelectedRoleId(role.id);

    setPermissions({
      ...role.permissions,
    });
  };

  /* =======================================================
     CHANGE PERMISSION
  ======================================================= */

  const handlePermissionChange = (
    moduleId,
    permission,
    checked
  ) => {
    setPermissions((previous) => ({
      ...previous,
      [moduleId]: {
        ...previous[moduleId],
        [permission]: checked,
      },
    }));
  };

  /* =======================================================
     SELECT ALL MODULE
  ======================================================= */

  const handleModuleAll = (
    moduleId,
    checked
  ) => {
    setPermissions((previous) => ({
      ...previous,
      [moduleId]: {
        view: checked,
        add: checked,
        edit: checked,
        delete: checked,
        export: checked,
      },
    }));
  };

  /* =======================================================
     SELECT ALL
  ======================================================= */

  const allSelected = modules.every(
    (module) =>
      permissionTypes.every(
        (permission) =>
          permissions[module.id]?.[
          permission
          ] === true
      )
  );

  const handleSelectAll = (checked) => {
    const updated =
      createPermissionObject();

    modules.forEach((module) => {
      permissionTypes.forEach(
        (permission) => {
          updated[module.id][
            permission
          ] = checked;
        }
      );
    });

    setPermissions(updated);
  };

  /* =======================================================
     MODULE FULL ACCESS
  ======================================================= */

  const isModuleFullAccess = (
    moduleId
  ) => {
    return permissionTypes.every(
      (permission) =>
        permissions[moduleId]?.[
        permission
        ] === true
    );
  };

  /* =======================================================
     MODULE PERMISSION COUNT
  ======================================================= */

  const getModulePermissionCount = (
    moduleId
  ) => {
    return permissionTypes.filter(
      (permission) =>
        permissions[moduleId]?.[
        permission
        ]
    ).length;
  };

  /* =======================================================
     PERMISSION COUNTS
  ======================================================= */

  const permissionCounts = {
    view: 0,
    add: 0,
    edit: 0,
    delete: 0,
    export: 0,
  };

  modules.forEach((module) => {
    permissionTypes.forEach(
      (permission) => {
        if (
          permissions[module.id]?.[
          permission
          ]
        ) {
          permissionCounts[
            permission
          ] += 1;
        }
      }
    );
  });

  const fullAccessModules =
    modules.filter((module) =>
      isModuleFullAccess(
        module.id
      )
    ).length;

  /* =======================================================
     SAVE PERMISSIONS
  ======================================================= */

  const handleSavePermissions = () => {
    if (!selectedRole) {
      return;
    }

    setRoles((previous) =>
      previous.map((role) =>
        role.id === selectedRole.id
          ? {
            ...role,
            permissions: {
              ...permissions,
            },
          }
          : role
      )
    );

    alert(
      `Permissions saved for ${selectedRole.name}`
    );
  };

  /* =======================================================
     ADD ROLE
  ======================================================= */

  const handleOpenAdd = () => {
    setEditingRole(null);

    setRoleForm({
      ...emptyRole,
    });

    setOpenDialog(true);
  };

  /* =======================================================
     EDIT ROLE
  ======================================================= */

  const handleOpenEdit = (role) => {
    setEditingRole(role);

    setRoleForm({
      name: role.name,
      description: role.description,
      status: role.status,
    });

    setOpenDialog(true);
  };

  /* =======================================================
     CLOSE DIALOG
  ======================================================= */

  const handleCloseDialog = () => {
    setOpenDialog(false);

    setEditingRole(null);

    setRoleForm({
      ...emptyRole,
    });
  };

  /* =======================================================
     ROLE FORM
  ======================================================= */

  const handleRoleFormChange = (
    field,
    value
  ) => {
    setRoleForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  /* =======================================================
     SAVE ROLE
  ======================================================= */

  const handleSaveRole = () => {
    if (!roleForm.name.trim()) {
      alert(
        "Please enter role name."
      );
      return;
    }

    if (
      !roleForm.description.trim()
    ) {
      alert(
        "Please enter role description."
      );
      return;
    }

    if (editingRole) {
      setRoles((previous) =>
        previous.map((role) =>
          role.id ===
            editingRole.id
            ? {
              ...role,
              name:
                roleForm.name.trim(),
              description:
                roleForm.description.trim(),
              status:
                roleForm.status,
            }
            : role
        )
      );

      handleCloseDialog();
      return;
    }

    const newRole = {
      id: Date.now(),
      name: roleForm.name.trim(),
      description:
        roleForm.description.trim(),
      users: 0,
      status: roleForm.status,
      type: "Custom",
      permissions:
        createPermissionObject(),
    };

    setRoles((previous) => [
      ...previous,
      newRole,
    ]);

    setSelectedRoleId(
      newRole.id
    );

    setPermissions(
      createPermissionObject()
    );

    handleCloseDialog();
  };

  /* =======================================================
     DELETE ROLE
  ======================================================= */

  const handleDeleteRole = (role) => {
    if (role.type === "System") {
      alert(
        "System roles cannot be deleted."
      );
      return;
    }

    const confirmed =
      window.confirm(
        `Are you sure you want to delete "${role.name}"?`
      );

    if (!confirmed) {
      return;
    }

    const updatedRoles =
      roles.filter(
        (item) =>
          item.id !== role.id
      );

    setRoles(updatedRoles);

    if (
      selectedRoleId === role.id
    ) {
      const nextRole =
        updatedRoles[0];

      if (nextRole) {
        setSelectedRoleId(
          nextRole.id
        );

        setPermissions({
          ...nextRole.permissions,
        });
      }
    }
  };

  /* =======================================================
     RETURN
  ======================================================= */

  return (
    <Box
      sx={{
        width: "100%",
        minWidth: 0,
        minHeight: "100%",
        backgroundColor: "#F6F8F9",
        px: {
          xs: 1.5,
          sm: 2,
          md: 2.5,
        },
        py: {
          xs: 1.5,
          md: 2,
        },
        boxSizing: "border-box",
      }}
    >
      {/* =================================================
          PAGE HEADER
      ================================================= */}

      <Box
  sx={{
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    mb: 2.5,
    gap: 2,
  }}
>
        <Box>
          <Typography
            sx={{
              fontSize: {
                xs: "20px",
                sm: "24px",
              },
              fontWeight: 700,
              color: TEXT,
              lineHeight: 1.3,
            }}
          >
            Roles & Permissions
          </Typography>

          <Typography
            sx={{
              mt: 0.5,
              fontSize: "13px",
              color: MUTED,
            }}
          >
            Manage roles and control
            module-wise access permissions
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={
            <PersonAddAltOutlined />
          }
          onClick={handleOpenAdd}
          sx={{
            flexShrink: 0,
            minWidth: 120,
            height: 42,
            px: 2.2,
            borderRadius: "6px",
            backgroundColor:
              PRIMARY,
            textTransform: "none",
            fontWeight: 600,
            boxShadow: "none",

            "&:hover": {
              backgroundColor:
                PRIMARY_DARK,
              boxShadow: "none",
            },

            "@media (max-width:600px)":
            {
              minWidth: "auto",
              px: 1.5,
            },
          }}
        >
          Add Role
        </Button>
      </Box>

      {/* =================================================
          SUMMARY
      ================================================= */}

      <Grid
        container
        spacing={2}
        sx={{
          mb: 2.5,
        }}
      >
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3,
          }}
        >
          <SummaryCard
            title="Total Roles"
            value={totalRoles}
            description="All configured roles"
            icon={
              <GroupsOutlined />
            }
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3,
          }}
        >
          <SummaryCard
            title="Active Roles"
            value={activeRoles}
            description="Currently active"
            icon={
              <SecurityOutlined />
            }
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3,
          }}
        >
          <SummaryCard
            title="Admin Roles"
            value={adminRoles}
            description="Administrative roles"
            icon={
              <AdminPanelSettingsOutlined />
            }
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3,
          }}
        >
          <SummaryCard
            title="Custom Roles"
            value={customRoles}
            description="Custom configured roles"
            icon={
              <ManageAccountsOutlined />
            }
          />
        </Grid>
      </Grid>

      {/* =================================================
          SEARCH
      ================================================= */}

      <Paper
        elevation={0}
        sx={{
          border:
            "1px solid #E1E7E8",
          borderRadius: "6px",
          backgroundColor:
            PAPER,
          p: 2,
          mb: 2,
        }}
      >
        <Grid
          container
          spacing={2}
        >
          <Grid
            size={{
              xs: 12,
              md: 8,
            }}
          >
            <TextField
              fullWidth
              size="small"
              value={search}
              onChange={(event) =>
                setSearch(
                  event.target.value
                )
              }
              placeholder="Search roles..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlined
                      sx={{
                        color:
                          "#98A2B3",
                      }}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root":
                {
                  borderRadius:
                    "6px",
                },
              }}
            />
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 4,
            }}
          >
            <FormControl
              fullWidth
              size="small"
            >
              <InputLabel>
                Status
              </InputLabel>

              <Select
                value={statusFilter}
                label="Status"
                onChange={(event) =>
                  setStatusFilter(
                    event.target.value
                  )
                }
                sx={{
                  borderRadius:
                    "6px",
                }}
              >
                <MenuItem value="All">
                  All Status
                </MenuItem>

                <MenuItem value="Active">
                  Active
                </MenuItem>

                <MenuItem value="Inactive">
                  Inactive
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* =================================================
          MASTER DETAIL
      ================================================= */}

      <Grid
        container
        spacing={2}
        alignItems="stretch"
      >
        {/* =================================================
            LEFT - ROLES
        ================================================= */}

        <Grid
          size={{
            xs: 12,
            lg: 4,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              height: "100%",
              border:
                "1px solid #E1E7E8",
              borderRadius: "6px",
              backgroundColor:
                PAPER,
              overflow: "hidden",
            }}
          >
            {/* HEADER */}

            <Box
              sx={{
                px: 2,
                py: 1.8,
                borderBottom:
                  "1px solid #E1E7E8",
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize:
                        "16px",
                      fontWeight: 700,
                      color:
                        TEXT,
                    }}
                  >
                    Roles
                  </Typography>

                  <Typography
                    sx={{
                      fontSize:
                        "12px",
                      color:
                        MUTED,
                      mt: 0.3,
                    }}
                  >
                    Select a role to
                    configure access
                  </Typography>
                </Box>

                <IconButton
                  size="small"
                  onClick={
                    handleOpenAdd
                  }
                  sx={{
                    color:
                      PRIMARY,
                    backgroundColor:
                      "#E8F5F5",
                    borderRadius:
                      "6px",

                    "&:hover": {
                      backgroundColor:
                        "#D8EEEE",
                    },
                  }}
                >
                  <AddOutlined />
                </IconButton>
              </Stack>
            </Box>

            {/* ROLE LIST */}

            <Box
              sx={{
                p: 1,
                maxHeight: {
                  xs: "none",
                  lg: 650,
                },
                overflowY:
                  "auto",
              }}
            >
              {filteredRoles.map(
                (role) => {
                  const active =
                    role.id ===
                    selectedRoleId;

                  return (
                    <Box
                      key={role.id}
                      onClick={() =>
                        handleSelectRole(
                          role
                        )
                      }
                      sx={{
                        p: 1.5,
                        mb: 0.8,
                        borderRadius:
                          "6px",
                        cursor:
                          "pointer",
                        border:
                          active
                            ? `1px solid ${PRIMARY_LIGHT}`
                            : "1px solid transparent",

                        backgroundColor:
                          active
                            ? "#EAF7F7"
                            : "#FFFFFF",

                        "&:hover": {
                          backgroundColor:
                            active
                              ? "#EAF7F7"
                              : "#F7FAFA",
                        },
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1.2}
                        alignItems="center"
                      >
                        {/* ICON */}

                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            flexShrink: 0,
                            borderRadius:
                              "6px",
                            display:
                              "flex",
                            alignItems:
                              "center",
                            justifyContent:
                              "center",
                            backgroundColor:
                              active
                                ? "#D7F1F1"
                                : "#F1F5F5",
                            color:
                              PRIMARY,
                          }}
                        >
                          <ShieldOutlined fontSize="small" />
                        </Box>

                        {/* INFO */}

                        <Box
                          sx={{
                            minWidth:
                              0,
                            flex: 1,
                          }}
                        >
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.7}
                          >
                            <Typography
                              sx={{
                                fontSize:
                                  "13px",
                                fontWeight: 700,
                                color:
                                  TEXT,
                                overflow:
                                  "hidden",
                                textOverflow:
                                  "ellipsis",
                                whiteSpace:
                                  "nowrap",
                              }}
                            >
                              {
                                role.name
                              }
                            </Typography>

                            {active && (
                              <Box
                                sx={{
                                  width: 6,
                                  height: 6,
                                  borderRadius:
                                    "50%",
                                  backgroundColor:
                                    PRIMARY,
                                  flexShrink: 0,
                                }}
                              />
                            )}
                          </Stack>

                          <Typography
                            sx={{
                              fontSize:
                                "11px",
                              color:
                                MUTED,
                              mt: 0.3,
                              overflow:
                                "hidden",
                              textOverflow:
                                "ellipsis",
                              whiteSpace:
                                "nowrap",
                            }}
                          >
                            {
                              role.description
                            }
                          </Typography>

                          <Stack
                            direction="row"
                            spacing={0.6}
                            sx={{
                              mt: 0.8,
                            }}
                          >
                            <Chip
                              label={
                                role.type
                              }
                              size="small"
                              sx={{
                                height:
                                  21,
                                borderRadius:
                                  "4px",
                                fontSize:
                                  "10px",
                                fontWeight: 600,
                                backgroundColor:
                                  role.type ===
                                    "System"
                                    ? "#E8F5F5"
                                    : "#F3EEFA",
                                color:
                                  role.type ===
                                    "System"
                                    ? PRIMARY_DARK
                                    : "#6B4EA0",
                              }}
                            />

                            <Chip
                              label={`${role.users} user${role.users !==
                                  1
                                  ? "s"
                                  : ""
                                }`}
                              size="small"
                              sx={{
                                height:
                                  21,
                                borderRadius:
                                  "4px",
                                fontSize:
                                  "10px",
                                fontWeight: 600,
                                backgroundColor:
                                  "#F4F6F7",
                                color:
                                  MUTED,
                              }}
                            />
                          </Stack>
                        </Box>

                        {/* EDIT */}

                        <IconButton
                          size="small"
                          onClick={(
                            event
                          ) => {
                            event.stopPropagation();

                            handleOpenEdit(
                              role
                            );
                          }}
                          sx={{
                            width: 30,
                            height: 30,
                            color:
                              PRIMARY,
                            borderRadius:
                              "5px",

                            "&:hover": {
                              backgroundColor:
                                "#DFF2F2",
                            },
                          }}
                        >
                          <EditOutlined
                            sx={{
                              fontSize:
                                17,
                            }}
                          />
                        </IconButton>
                      </Stack>
                    </Box>
                  );
                }
              )}

              {filteredRoles.length ===
                0 && (
                  <Box
                    sx={{
                      py: 6,
                      textAlign:
                        "center",
                    }}
                  >
                    <ShieldOutlined
                      sx={{
                        fontSize: 40,
                        color:
                          "#B8C3C5",
                      }}
                    />

                    <Typography
                      sx={{
                        mt: 1,
                        fontSize:
                          "14px",
                        fontWeight: 600,
                      }}
                    >
                      No roles found
                    </Typography>
                  </Box>
                )}
            </Box>
          </Paper>
        </Grid>

        {/* =================================================
            RIGHT - ROLE DETAILS
        ================================================= */}

        <Grid
          size={{
            xs: 12,
            lg: 8,
          }}
        >
          {selectedRole && (
            <Paper
              elevation={0}
              sx={{
                height: "100%",
                border:
                  "1px solid #E1E7E8",
                borderRadius: "6px",
                backgroundColor:
                  PAPER,
                overflow: "hidden",
              }}
            >
              {/* ===========================================
                  ROLE HEADER
              ============================================ */}

              <Box
                sx={{
                  p: 2.2,
                  borderBottom:
                    "1px solid #E1E7E8",
                  background:
                    "linear-gradient(180deg, #F7FCFC 0%, #FFFFFF 100%)",
                }}
              >
                <Stack
                  direction={{
                    xs: "column",
                    sm: "row",
                  }}
                  alignItems={{
                    xs: "flex-start",
                    sm: "center",
                  }}
                  justifyContent="space-between"
                  spacing={2}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                  >
                    <Box
                      sx={{
                        width: 50,
                        height: 50,
                        flexShrink: 0,
                        borderRadius:
                          "8px",
                        display:
                          "flex",
                        alignItems:
                          "center",
                        justifyContent:
                          "center",
                        backgroundColor:
                          "#DFF3F3",
                        color:
                          PRIMARY,
                      }}
                    >
                      <ShieldOutlined
                        sx={{
                          fontSize:
                            27,
                        }}
                      />
                    </Box>

                    <Box>
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        flexWrap="wrap"
                      >
                        <Typography
                          sx={{
                            fontSize:
                              "18px",
                            fontWeight: 700,
                            color:
                              TEXT,
                          }}
                        >
                          {
                            selectedRole.name
                          }
                        </Typography>

                        <Chip
                          label={
                            selectedRole.type
                          }
                          size="small"
                          sx={{
                            height:
                              24,
                            borderRadius:
                              "5px",
                            fontSize:
                              "10px",
                            fontWeight: 700,
                            backgroundColor:
                              selectedRole.type ===
                                "System"
                                ? "#E8F5F5"
                                : "#F3EEFA",
                            color:
                              selectedRole.type ===
                                "System"
                                ? PRIMARY_DARK
                                : "#6B4EA0",
                          }}
                        />
                      </Stack>

                      <Typography
                        sx={{
                          fontSize:
                            "12px",
                          color:
                            MUTED,
                          mt: 0.5,
                        }}
                      >
                        {
                          selectedRole.description
                        }
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                  >
                    <Chip
                      label={
                        selectedRole.status
                      }
                      size="small"
                      sx={{
                        height:
                          27,
                        borderRadius:
                          "5px",
                        fontSize:
                          "11px",
                        fontWeight: 700,
                        backgroundColor:
                          selectedRole.status ===
                            "Active"
                            ? "#E8F6F1"
                            : "#FCECEC",
                        color:
                          selectedRole.status ===
                            "Active"
                            ? "#16845B"
                            : "#C62828",
                      }}
                    />

                    <Button
                      size="small"
                      startIcon={
                        <EditOutlined />
                      }
                      onClick={() =>
                        handleOpenEdit(
                          selectedRole
                        )
                      }
                      sx={{
                        height:
                          34,
                        borderRadius:
                          "5px",
                        color:
                          PRIMARY,
                        textTransform:
                          "none",
                        fontWeight: 600,
                      }}
                    >
                      Edit Role
                    </Button>
                  </Stack>
                </Stack>

                {/* ROLE META */}

                <Stack
                  direction="row"
                  spacing={3}
                  flexWrap="wrap"
                  sx={{
                    mt: 2,
                  }}
                >
                  <MetaItem
                    icon={
                      <GroupsOutlined />
                    }
                    label="Users"
                    value={
                      selectedRole.users
                    }
                  />

                  <MetaItem
                    icon={
                      <LockOutlined />
                    }
                    label="Role Type"
                    value={
                      selectedRole.type
                    }
                  />

                  <MetaItem
                    icon={
                      <SecurityOutlined />
                    }
                    label="Status"
                    value={
                      selectedRole.status
                    }
                  />
                </Stack>
              </Box>

              {/* ===========================================
                  PERMISSION SUMMARY
              ============================================ */}

              <Box
                sx={{
                  p: 2,
                  borderBottom:
                    "1px solid #E1E7E8",
                }}
              >
                <Typography
                  sx={{
                    fontSize:
                      "14px",
                    fontWeight: 700,
                    color:
                      TEXT,
                    mb: 1.2,
                  }}
                >
                  Permission Overview
                </Typography>

                <Grid
                  container
                  spacing={1.2}
                >
                  <Grid
                    size={{
                      xs: 6,
                      sm: 3,
                    }}
                  >
                    <MiniStat
                      value={
                        fullAccessModules
                      }
                      label="Full Access"
                    />
                  </Grid>

                  <Grid
                    size={{
                      xs: 6,
                      sm: 3,
                    }}
                  >
                    <MiniStat
                      value={
                        permissionCounts
                          .view
                      }
                      label="View"
                    />
                  </Grid>

                  <Grid
                    size={{
                      xs: 6,
                      sm: 3,
                    }}
                  >
                    <MiniStat
                      value={
                        permissionCounts
                          .add
                      }
                      label="Add"
                    />
                  </Grid>

                  <Grid
                    size={{
                      xs: 6,
                      sm: 3,
                    }}
                  >
                    <MiniStat
                      value={
                        permissionCounts
                          .edit
                      }
                      label="Edit"
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* ===========================================
                  PERMISSIONS HEADER
              ============================================ */}

              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  borderBottom:
                    "1px solid #E1E7E8",
                  backgroundColor:
                    "#FAFCFC",
                }}
              >
                <Stack
                  direction={{
                    xs: "column",
                    sm: "row",
                  }}
                  alignItems={{
                    xs: "flex-start",
                    sm: "center",
                  }}
                  justifyContent="space-between"
                  spacing={1}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize:
                          "15px",
                        fontWeight: 700,
                        color:
                          TEXT,
                      }}
                    >
                      Module Permissions
                    </Typography>

                    <Typography
                      sx={{
                        fontSize:
                          "11px",
                        color:
                          MUTED,
                        mt: 0.3,
                      }}
                    >
                      Control access for
                      each CRM module
                    </Typography>
                  </Box>

                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.8}
                  >
                    <Typography
                      sx={{
                        fontSize:
                          "12px",
                        fontWeight: 600,
                        color:
                          TEXT,
                      }}
                    >
                      Select All
                    </Typography>

                    <Checkbox
                      size="small"
                      checked={
                        allSelected
                      }
                      onChange={(
                        event
                      ) =>
                        handleSelectAll(
                          event
                            .target
                            .checked
                        )
                      }
                      sx={{
                        color:
                          PRIMARY,

                        "&.Mui-checked":
                        {
                          color:
                            PRIMARY,
                        },
                      }}
                    />
                  </Stack>
                </Stack>
              </Box>

              {/* ===========================================
                  MODULE CARDS
              ============================================ */}

              <Box
                sx={{
                  p: 1.5,
                  maxHeight: {
                    xs: "none",
                    lg: 490,
                  },
                  overflowY:
                    "auto",
                }}
              >
                <Stack spacing={1}>
                  {modules.map(
                    (module) => {
                      const count =
                        getModulePermissionCount(
                          module.id
                        );

                      const full =
                        count ===
                        permissionTypes.length;

                      return (
                        <Box
                          key={
                            module.id
                          }
                          sx={{
                            border:
                              "1px solid #E5E9EA",
                            borderRadius:
                              "6px",
                            p: 1.3,
                            backgroundColor:
                              full
                                ? "#FAFEFE"
                                : "#FFFFFF",
                          }}
                        >
                          {/* MODULE TOP */}

                          <Stack
                            direction={{
                              xs: "column",
                              sm: "row",
                            }}
                            alignItems={{
                              xs: "flex-start",
                              sm: "center",
                            }}
                            justifyContent="space-between"
                            spacing={1}
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1.1}
                            >
                              <Box
                                sx={{
                                  width: 34,
                                  height: 34,
                                  flexShrink: 0,
                                  borderRadius:
                                    "6px",
                                  display:
                                    "flex",
                                  alignItems:
                                    "center",
                                  justifyContent:
                                    "center",
                                  backgroundColor:
                                    full
                                      ? "#DFF3F3"
                                      : "#F1F5F5",
                                  color:
                                    PRIMARY,
                                }}
                              >
                                <SettingsOutlined
                                  sx={{
                                    fontSize:
                                      18,
                                  }}
                                />
                              </Box>

                              <Box>
                                <Typography
                                  sx={{
                                    fontSize:
                                      "13px",
                                    fontWeight: 700,
                                    color:
                                      TEXT,
                                  }}
                                >
                                  {
                                    module.name
                                  }
                                </Typography>

                                <Typography
                                  sx={{
                                    fontSize:
                                      "10px",
                                    color:
                                      MUTED,
                                    mt: 0.2,
                                  }}
                                >
                                  {
                                    module.description
                                  }
                                </Typography>
                              </Box>
                            </Stack>

                            <Chip
                              label={
                                full
                                  ? "Full Access"
                                  : `${count}/5 Permissions`
                              }
                              size="small"
                              sx={{
                                height:
                                  24,
                                borderRadius:
                                  "5px",
                                fontSize:
                                  "10px",
                                fontWeight: 700,
                                backgroundColor:
                                  full
                                    ? "#E8F6F1"
                                    : "#F4F6F7",
                                color:
                                  full
                                    ? "#16845B"
                                    : MUTED,
                              }}
                            />
                          </Stack>

                          {/* PERMISSION CONTROLS */}

                          <Box
                            sx={{
                              mt: 1.2,
                              pt: 1.1,
                              borderTop:
                                "1px solid #EEF1F2",
                            }}
                          >
                            <Grid
                              container
                              spacing={{
                                xs: 0.5,
                                sm: 1,
                              }}
                            >
                              {permissionTypes.map(
                                (
                                  permission
                                ) => (
                                  <Grid
                                    key={
                                      permission
                                    }
                                    size={{
                                      xs: 6,
                                      sm: "auto",
                                    }}
                                  >
                                    <PermissionItem
                                      label={
                                        permission
                                          .charAt(
                                            0
                                          )
                                          .toUpperCase() +
                                        permission.slice(
                                          1
                                        )
                                      }
                                      checked={
                                        permissions[
                                        module
                                          .id
                                        ]?.[
                                        permission
                                        ]
                                      }
                                      onChange={(
                                        checked
                                      ) =>
                                        handlePermissionChange(
                                          module.id,
                                          permission,
                                          checked
                                        )
                                      }
                                    />
                                  </Grid>
                                )
                              )}

                              <Grid
                                size={{
                                  xs: 6,
                                  sm: "auto",
                                }}
                              >
                                <PermissionItem
                                  label="All"
                                  checked={full}
                                  onChange={(
                                    checked
                                  ) =>
                                    handleModuleAll(
                                      module.id,
                                      checked
                                    )
                                  }
                                />
                              </Grid>
                            </Grid>
                          </Box>
                        </Box>
                      );
                    }
                  )}
                </Stack>
              </Box>

              {/* ===========================================
                  SAVE BAR
              ============================================ */}

              <Divider />

              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  display: "flex",
                  justifyContent:
                    "flex-end",
                  backgroundColor:
                    "#FAFCFC",
                }}
              >
                <Button
                  variant="contained"
                  startIcon={
                    <SecurityOutlined />
                  }
                  onClick={
                    handleSavePermissions
                  }
                  sx={{
                    minWidth: 155,
                    height: 40,
                    borderRadius:
                      "6px",
                    backgroundColor:
                      PRIMARY,
                    textTransform:
                      "none",
                    fontWeight: 600,
                    boxShadow: "none",

                    "&:hover": {
                      backgroundColor:
                        PRIMARY_DARK,
                      boxShadow:
                        "none",
                    },
                  }}
                >
                  Save Permissions
                </Button>
              </Box>
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* =================================================
          ADD / EDIT ROLE DIALOG
      ================================================= */}

      <Dialog
        open={openDialog}
        onClose={
          handleCloseDialog
        }
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius:
              "8px",
            overflow:
              "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 3,
            py: 2.2,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box>
              <Typography
                sx={{
                  fontSize:
                    "19px",
                  fontWeight: 700,
                  color:
                    TEXT,
                }}
              >
                {editingRole
                  ? "Edit Role"
                  : "Add New Role"}
              </Typography>

              <Typography
                sx={{
                  fontSize:
                    "13px",
                  color:
                    MUTED,
                  mt: 0.5,
                }}
              >
                {editingRole
                  ? "Update role information"
                  : "Create a custom CRM role"}
              </Typography>
            </Box>

            <IconButton
              onClick={
                handleCloseDialog
              }
              sx={{
                borderRadius:
                  "6px",
              }}
            >
              <CloseOutlined />
            </IconButton>
          </Stack>
        </DialogTitle>

        <Divider />

        <DialogContent
          sx={{
            px: 3,
            py: 3,
          }}
        >
          <Stack spacing={2}>
            <TextField
              fullWidth
              size="small"
              label="Role Name"
              value={
                roleForm.name
              }
              onChange={(event) =>
                handleRoleFormChange(
                  "name",
                  event.target.value
                )
              }
              placeholder="Example: Sales Manager"
              sx={{
                "& .MuiOutlinedInput-root":
                {
                  borderRadius:
                    "6px",
                },
              }}
            />

            <TextField
              fullWidth
              multiline
              rows={3}
              size="small"
              label="Description"
              value={
                roleForm.description
              }
              onChange={(event) =>
                handleRoleFormChange(
                  "description",
                  event.target.value
                )
              }
              placeholder="Describe this role..."
              sx={{
                "& .MuiOutlinedInput-root":
                {
                  borderRadius:
                    "6px",
                },
              }}
            />

            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                border:
                  "1px solid #E1E7E8",
                borderRadius:
                  "6px",
                backgroundColor:
                  "#FAFCFC",
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize:
                        "14px",
                      fontWeight: 600,
                    }}
                  >
                    Role Status
                  </Typography>

                  <Typography
                    sx={{
                      fontSize:
                        "12px",
                      color:
                        MUTED,
                      mt: 0.3,
                    }}
                  >
                    Allow users assigned
                    to this role to access
                    the CRM
                  </Typography>
                </Box>

                <Switch
                  checked={
                    roleForm.status ===
                    "Active"
                  }
                  onChange={(event) =>
                    handleRoleFormChange(
                      "status",
                      event.target
                        .checked
                        ? "Active"
                        : "Inactive"
                    )
                  }
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked":
                    {
                      color:
                        PRIMARY,
                    },

                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                    {
                      backgroundColor:
                        PRIMARY,
                    },
                  }}
                />
              </Stack>
            </Paper>
          </Stack>
        </DialogContent>

        <Divider />

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            gap: 1,
          }}
        >
          <Button
            onClick={
              handleCloseDialog
            }
            sx={{
              minWidth: 90,
              height: 40,
              borderRadius:
                "6px",
              textTransform:
                "none",
              fontWeight: 600,
              color: TEXT,
              backgroundColor:
                "#F4F7F7",

              "&:hover": {
                backgroundColor:
                  "#E9EEEE",
              },
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={
              handleSaveRole
            }
            startIcon={
              editingRole ? (
                <EditOutlined />
              ) : (
                <PersonAddAltOutlined />
              )
            }
            sx={{
              minWidth: 125,
              height: 40,
              borderRadius:
                "6px",
              backgroundColor:
                PRIMARY,
              textTransform:
                "none",
              fontWeight: 600,
              boxShadow: "none",

              "&:hover": {
                backgroundColor:
                  PRIMARY_DARK,
                boxShadow: "none",
              },
            }}
          >
            {editingRole
              ? "Update Role"
              : "Create Role"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/* =========================================================
   SUMMARY CARD
========================================================= */

function SummaryCard({
  title,
  value,
  description,
  icon,
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border:
          "1px solid #E1E7E8",
        borderRadius: "6px",
        backgroundColor:
          PAPER,
        height: "100%",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <Box>
          <Typography
            sx={{
              fontSize:
                "13px",
              color:
                MUTED,
            }}
          >
            {title}
          </Typography>

          <Typography
            sx={{
              fontSize:
                "26px",
              fontWeight: 700,
              color:
                TEXT,
              lineHeight: 1.2,
              mt: 0.3,
            }}
          >
            {value}
          </Typography>

          <Typography
            sx={{
              fontSize:
                "12px",
              color:
                MUTED,
              mt: 0.5,
            }}
          >
            {description}
          </Typography>
        </Box>

        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius:
              "6px",
            display:
              "flex",
            alignItems:
              "center",
            justifyContent:
              "center",
            backgroundColor:
              "#E8F5F5",
            color:
              PRIMARY,
          }}
        >
          {icon}
        </Box>
      </Stack>
    </Paper>
  );
}

/* =========================================================
   META ITEM
========================================================= */

function MetaItem({
  icon,
  label,
  value,
}) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.8}
    >
      <Box
        sx={{
          color: PRIMARY,
          display: "flex",
        }}
      >
        {React.cloneElement(
          icon,
          {
            sx: {
              fontSize: 17,
            },
          }
        )}
      </Box>

      <Box>
        <Typography
          sx={{
            fontSize:
              "10px",
            color:
              "#98A2B3",
            lineHeight: 1,
          }}
        >
          {label}
        </Typography>

        <Typography
          sx={{
            fontSize:
              "12px",
            fontWeight: 600,
            color:
              TEXT,
            mt: 0.3,
          }}
        >
          {value}
        </Typography>
      </Box>
    </Stack>
  );
}

/* =========================================================
   MINI STAT
========================================================= */

function MiniStat({
  value,
  label,
}) {
  return (
    <Box
      sx={{
        p: 1.2,
        border:
          "1px solid #E8ECEE",
        borderRadius:
          "6px",
        backgroundColor:
          "#FAFCFC",
      }}
    >
      <Typography
        sx={{
          fontSize:
            "19px",
          fontWeight: 700,
          color:
            PRIMARY,
        }}
      >
        {value}
      </Typography>

      <Typography
        sx={{
          fontSize:
            "10px",
          color:
            MUTED,
          mt: 0.2,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

/* =========================================================
   PERMISSION ITEM
========================================================= */

function PermissionItem({
  label,
  checked,
  onChange,
}) {
  return (
    <Box
      sx={{
        display:
          "flex",
        alignItems:
          "center",
        minWidth: {
          sm: 82,
        },
        px: 0.6,
        py: 0.2,
        borderRadius:
          "5px",
        backgroundColor:
          checked
            ? "#F0FAFA"
            : "transparent",
      }}
    >
      <Checkbox
        size="small"
        checked={Boolean(
          checked
        )}
        onChange={(event) =>
          onChange(
            event.target
              .checked
          )
        }
        sx={{
          p: 0.5,
          color:
            "#B8C3C5",

          "&.Mui-checked": {
            color:
              PRIMARY,
          },
        }}
      />

      <Typography
        sx={{
          fontSize:
            "10px",
          fontWeight:
            checked
              ? 600
              : 400,
          color:
            checked
              ? PRIMARY_DARK
              : MUTED,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

export default RolesPermissions;